/**
 * Date/time formatting helpers. Operator console aesthetic: relative for
 * recent items, absolute for older ones.
 */

export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  if (diffDay < 365) {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * PII-safe display for an identity's `external_id` field.
 *
 * Background: thread participants/contact identities expose the
 * channel-side external id (e.g. `li:user:alex-doe-12345`,
 * `wa:+15555550100`, `tg:user:1234567`, `ping@dmitry-fedotov.com`).
 * The LinkedIn shape contains no PII by itself (the URN is opaque),
 * but WhatsApp uses raw E.164 phone numbers, Telegram occasionally
 * uses phones too, and Gmail/IMAP uses raw email addresses. Showing
 * phones or full email addresses in a sidebar is a leak surface even
 * on a single-operator console — a colleague glancing at the screen
 * sees the contact.
 *
 * Policy:
 *   - linkedin / instagram / messenger: show verbatim (no PII in
 *     the standard external_id shape).
 *   - whatsapp / telegram: if the external_id contains a `+\d{8,}`
 *     phone-like substring, mask all but the first 2 and last 2 of
 *     the digit run. Other external_ids on those channels (e.g.
 *     Telegram numeric user ids) pass through with the same mask.
 *   - email: mask the local-part of any RFC-5322-shaped address —
 *     show only the first 2 chars + bullets + `@domain`. The domain
 *     stays visible because operators routinely use it to identify
 *     who the message is from at a glance (newsletters, vendor
 *     domains, internal vs external).
 *
 * Tested in apps/web/src/lib/format.test.ts to pin the masking
 * contract.
 */
export function maskExternalIdForDisplay(channel: string, externalId: string): string {
  if (!externalId) return externalId;
  // Email channel: mask the local-part of address-shaped tokens.
  // Captures `name@domain` anywhere in the string (the Gmail shape
  // stores the bare address as the identifier; legacy IMAP uses
  // `email:name@domain` — both work because the regex looks for the
  // `@` anchor).
  if (channel === 'email') {
    return externalId.replace(
      /([A-Za-z0-9._+-]{1,64})@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/g,
      (_, local: string, domain: string) => {
        if (local.length <= 2) return `${local}@${domain}`;
        const head = local.slice(0, 2);
        const masked = '•'.repeat(Math.min(8, Math.max(2, local.length - 2)));
        return `${head}${masked}@${domain}`;
      },
    );
  }
  // Only the remaining mask path covers phone-like channels.
  if (channel !== 'whatsapp' && channel !== 'telegram') return externalId;
  // Find a contiguous run of digits >= 8 chars (E.164 minimum is
  // ~7–8 digits + country code; 8 is a safe lower bound to avoid
  // masking random short ids).
  return externalId.replace(/\+?(\d{8,})/g, (_, digits: string) => {
    if (digits.length <= 4) return digits;
    const head = digits.slice(0, 2);
    const tail = digits.slice(-2);
    const middle = '•'.repeat(Math.max(2, digits.length - 4));
    return `+${head}${middle}${tail}`;
  });
}

export function formatChannel(channel: string): string {
  switch (channel) {
    case 'linkedin':
      return 'LinkedIn';
    case 'whatsapp':
      return 'WhatsApp';
    case 'email':
      return 'Email';
    case 'telegram':
      return 'Telegram';
    case 'instagram':
      return 'Instagram';
    case 'messenger':
      return 'Messenger';
    default:
      return channel || 'unknown';
  }
}

export interface DisplayContact {
  label: string;
  secondary: string | null;
}

import type { ParticipantDto } from '../types.js';

/**
 * Pick a "counterparty" representation for a thread. Heuristic: the first
 * participant whose handle/external_id does not look like the operator's
 * self id ("operator-*", "*self*"). Falls back to the first participant.
 */
export function counterpartyOf(participants: ParticipantDto[]): DisplayContact {
  const isOperatorish = (p: ParticipantDto) => {
    const hay = `${p.external_id} ${p.handle ?? ''}`.toLowerCase();
    return hay.includes('operator') || hay.includes('self');
  };
  // Length is checked above, then find() may still return undefined when
  // every participant is operator-ish. Fall back to the first row in that
  // case, or to the "Unknown contact" sentinel when there are zero rows.
  const target = participants.find((p) => !isOperatorish(p)) ?? participants[0];
  if (!target) {
    return { label: 'Unknown contact', secondary: null };
  }
  // batch-10 (C2) + batch-12: external_id / handle — and `display_name` itself
  // when the contact was never given a real name and it IS an email/phone — can
  // be raw PII (a WhatsApp E.164, a full address). Run all three through
  // `maskExternalIdForDisplay`, which is a no-op for real names / non-PII shapes
  // (LinkedIn URNs, opaque ids) and only masks email/phone-shaped values.
  const maskedDisplay = target.display_name
    ? maskExternalIdForDisplay(target.channel, target.display_name)
    : null;
  const maskedHandle = target.handle
    ? maskExternalIdForDisplay(target.channel, target.handle)
    : null;
  const maskedExternal = maskExternalIdForDisplay(target.channel, target.external_id);
  const label = maskedDisplay ?? maskedHandle ?? maskedExternal;
  const fallback = maskedHandle ?? maskedExternal;
  const secondary = maskedDisplay && fallback !== maskedDisplay ? fallback : null;
  return { label, secondary };
}

/**
 * Display label for a whole thread. Prefers the linked counterparty
 * (`counterpartyOf`); when none is linked, falls back to the chat/group title
 * (`subject`) — Telegram groups and channels have no single counterparty
 * contact, so the group name is the correct label. Drops to "Unknown contact"
 * only when neither is available. The subject is run through
 * `maskExternalIdForDisplay` (a no-op for real names; masks an email/phone-shaped
 * title) for the same PII safety as counterpartyOf.
 */
export function threadLabel(
  participants: ParticipantDto[],
  subject: string | null | undefined,
  channel: string,
): DisplayContact {
  const cp = counterpartyOf(participants);
  if (cp.label !== 'Unknown contact') return cp;
  const trimmed = subject?.trim();
  if (trimmed) {
    return { label: maskExternalIdForDisplay(channel, trimmed), secondary: null };
  }
  return cp;
}
